import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { readContract } from '@wagmi/core';

import type { Address } from 'viem';

// eslint-disable-next-line deprecate/import
import { wagmiConfig } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import { DEFAULT_TARGET_CHAIN, tokensInfo } from '@shared/constants/wallets';
import { convertTimestampSecondsToMs } from '@shared/helpers/date';
import { concatCurrencyToAmount } from '@shared/helpers/web3/formatters';
import { weiToNumber } from '@shared/helpers/web3/formatters/numberToWei';

import { formatReward } from '@ui/uikit/utils';

import { RAFFLE_CONTRACT } from '../contract';
import { getQueryKey } from './config';
import type { AmountInfo } from './types';

export type RaffleRoundsItem = {
  roundId: bigint;
  ticketsAmount: bigint;
  prizeInSqr: AmountInfo;
  prizeInUsdt: AmountInfo;
  closeTimestamp: number;
  isPrizeDispersed: boolean;
};

type Props = {
  walletAddress?: Address;
};

const getRounds = async ({ walletAddress }: Props): Promise<RaffleRoundsItem[]> => {
  if (!walletAddress) {
    return [];
  }

  try {
    const response = await readContract(wagmiConfig, {
      ...RAFFLE_CONTRACT,
      chainId: DEFAULT_TARGET_CHAIN.id,
      functionName: 'getWalletTicketsRoundInfo',
      args: [walletAddress],
    });

    return response
      .map((roundInfo) => {
        const prizeInSqr = roundInfo.prizeSqr;
        const prizeInUsdt = roundInfo.prizeUsdt;
        const sqrToken = tokensInfo.SQR;
        const usdtToken = tokensInfo.USDT;
        const prizeSqrNumber = weiToNumber(prizeInSqr, sqrToken.decimals);
        const prizeUsdtNumber = weiToNumber(prizeInUsdt, usdtToken.decimals);

        return {
          roundId: roundInfo.roundId,
          ticketsAmount: roundInfo.numTickets,
          prizeInSqr: {
            wei: prizeInSqr,
            value: prizeSqrNumber,
            formatted: concatCurrencyToAmount(formatReward(prizeSqrNumber), sqrToken.symbol),
          },
          prizeInUsdt: {
            wei: prizeInUsdt,
            value: prizeUsdtNumber,
            formatted: concatCurrencyToAmount(formatReward(prizeUsdtNumber), usdtToken.symbol),
          },
          closeTimestamp: convertTimestampSecondsToMs(roundInfo.timeClosed),
          isPrizeDispersed: roundInfo.prizeDispersed,
        };
      })
      .reverse();
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const useRounds = (
  walletAddress?: Address,
  options?: UseQueryOptions<RaffleRoundsItem[]>,
) => {
  return useQuery({
    queryKey: getQueryKey('rounds', walletAddress),
    queryFn: () => getRounds({ walletAddress }),
    ...options,
  });
};
